import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, FileCheck2, Upload, X } from 'lucide-react'
import { kit } from './strings'
import './FileField.css'

/** What the browser can draw in an `<img>` — everything else keeps the plain row. */
const PREVIEWABLE = /\.(png|jpe?g|gif|webp|avif|svg|ico|bmp)$/i
const isImage = (file) => Boolean(file) && (file.type?.startsWith('image/') || PREVIEWABLE.test(file.name))

/** "840 KB", "2.4 MB" — enough to tell a logo from a 40 MB package at a glance. */
const formatSize = (bytes) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

/**
 * File picker with drag-and-drop. Wraps a native file input so the trigger and the
 * "no file" hint show in Arabic rather than the browser's own locale.
 *
 * NO PROGRESS BAR. Choosing a file used to start one — a determinate bar with a
 * percentage, simulated by a timer when nothing was actually being sent, and driven by
 * `XHR.upload.onprogress` when something was. Both were misleading in the same way:
 * nothing is transferred when a file is PICKED (the form posts on save), so the bar
 * announced work that had not started and finished before it did.
 *
 * What the control says now is what it knows: which file is chosen. The save button
 * carries the waiting, which is where the waiting actually is.
 *
 * IT ALSO SHOWS THE FILE. A logo, a background, a favicon — the whole point of the
 * upload is what the thing LOOKS like, and until now the only way to find out was to
 * save it and go and look at the screen it lands on. A picked image is drawn from an
 * object URL before anything is sent; `currentUrl` draws the one already in place
 * beside it, so a replacement is a before and an after rather than a filename.
 *
 * @param {string} [currentUrl]  the file already in place, if the caller knows where it
 *   is served from. A URL that fails to load is dropped rather than drawn broken — on a
 *   fresh install there may be nothing there yet.
 * @param {'checker'|'light'|'dark'|'white'|'transparent'} [previewGround]  what to draw a transparent image
 *   on. A white logo on white paper is an empty box, so its card asks for `dark`.
 * @param {string[]} [formats]  the extensions the field takes, e.g. ['XLSX', 'XLS'] — shown
 *   as small tags beside the drop hint while nothing is chosen.
 */
export default function FileField({
  id,
  name,
  accept,
  required,
  onChange,
  buttonLabel = kit.chooseFile,
  emptyLabel = kit.noFile,
  currentUrl,
  previewGround = 'checker',
  formats,
}) {
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [dragging, setDragging] = useState(false)
  /* An object URL, not a data URL: nothing is read or copied, and it is released the
     moment the pick changes. A 2 MB background would otherwise be base64'd into memory
     twice — once here and once again by the save. */
  const [pickedUrl, setPickedUrl] = useState('')
  const [currentBroken, setCurrentBroken] = useState(false)

  useEffect(() => {
    if (!isImage(file)) {
      setPickedUrl('')
      return undefined
    }
    const url = URL.createObjectURL(file)
    setPickedUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  /* A new address is a new file: give it its chance to load before hiding it. */
  useEffect(() => {
    setCurrentBroken(false)
  }, [currentUrl])

  function select(next) {
    setFile(next ?? null)
    onChange?.(next ?? null)
  }

  /* A pick can be taken back without picking something else. The button sits inside
     the label, which leaves it alone (a label ignores clicks on its own controls);
     the native input is emptied too, so a form post does not carry the file. */
  function clear(event) {
    event.preventDefault()
    if (inputRef.current) inputRef.current.value = ''
    select(null)
  }

  /** Mirrors the browser's own `accept` filtering for dropped files. */
  function isAccepted(candidate) {
    if (!accept) return true
    return accept
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .some((part) =>
        part.startsWith('.')
          ? candidate.name.toLowerCase().endsWith(part)
          : part === '*/*' || (part.endsWith('/*')
            ? candidate.type.toLowerCase().startsWith(part.slice(0, -1))
            : candidate.type.toLowerCase() === part),
      )
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragging(false)

    const dropped = e.dataTransfer.files?.[0]
    if (!dropped || !isAccepted(dropped)) return

    // Keep the native input in sync so the control stays form-associated.
    const transfer = new DataTransfer()
    transfer.items.add(dropped)
    if (inputRef.current) inputRef.current.files = transfer.files

    select(dropped)
  }

  const selected = Boolean(file)
  const showCurrent = Boolean(currentUrl) && !currentBroken
  const showPreview = Boolean(pickedUrl) || showCurrent

  return (
    <label
      className={[
        'file-field',
        selected && 'file-field--selected',
        dragging && 'file-field--dragging',
        showPreview && 'file-field--previewing',
      ]
        .filter(Boolean)
        .join(' ')}
      htmlFor={id}
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        accept={accept}
        required={required}
        className="file-field__input"
        onChange={(e) => select(e.target.files?.[0] ?? null)}
        data-testid="file-field"
      />

      {showPreview && (
        <span className={`file-field__preview file-field__preview--${previewGround}`}>
          {/* Words only when there are two to tell apart — "current" against "new". Alone,
              the image in place needs no caption: it is the field's file. */}
          {showCurrent && (
            <Shot
              url={currentUrl}
              label={pickedUrl ? kit.currentFile : null}
              onError={() => setCurrentBroken(true)}
            />
          )}

          {/* Only between two of them: the arrow is "replaced by", not decoration. */}
          {showCurrent && pickedUrl && (
            <ArrowLeft className="file-field__arrow ltr-flip" size={16} strokeWidth={2} aria-hidden="true" />
          )}

          {pickedUrl && <Shot url={pickedUrl} label={kit.newFile} fresh />}
        </span>
      )}

      <span className="file-field__row">
        <span className="file-field__icon" aria-hidden="true">
          {selected ? <FileCheck2 size={18} /> : <Upload size={18} />}
        </span>

        <span className="file-field__body">
          <span className={`file-field__name${selected ? '' : ' file-field__name--empty'}`}>
            <bdi>{file?.name || emptyLabel}</bdi>
          </span>
          <span className="file-field__meta">
            {selected ? (
              <bdi dir="ltr">{formatSize(file.size)}</bdi>
            ) : (
              <>
                <span>{kit.dropHint}</span>
                {formats?.map((format) => (
                  <span key={format} className="file-field__format" dir="ltr">
                    {format}
                  </span>
                ))}
              </>
            )}
          </span>
        </span>

        {selected && (
          <button
            type="button"
            className="file-field__remove"
            aria-label={kit.removeFile}
            title={kit.removeFile}
            onClick={clear}
          >
            <X size={15} strokeWidth={2} />
          </button>
        )}

        <span className="file-field__action">{buttonLabel}</span>
      </span>
    </label>
  )
}

/** One image with its word under it. */
function Shot({ url, label, fresh = false, onError }) {
  return (
    <span className={`file-field__shot${fresh ? ' file-field__shot--new' : ''}`}>
      <span className="file-field__frame">
        {/* Decorative: the word beneath names it, and the field's own label names the
            field — an alt here would say the same thing a third time. */}
        <img src={url} alt="" onError={onError} />
      </span>
      {label && (
        <span className="file-field__shot-label">
          <bdi>{label}</bdi>
        </span>
      )}
    </span>
  )
}
